/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { useFormatDateByUserProfileLocation } from '@leagueplatform/web-common';
import { MasonryChartInterval } from '#types/masonry-chart.type.ts';
import { useGetDefaultTimezone } from '#hooks/use-get-default-timezone.hook.ts';
import * as LineGraphTypes from './types/line-graph';
import { MasonryLineGraph } from './masonry-line-graph';
import { formatMonthlyTick } from '../utils/use-monthly-ticks';
import {
  getIntervalXAxisTicks,
  getIntervalXAxisLabelTicks,
} from '../utils/get-interval-x-axis-ticks';

export type MasonryLineGraphProps<Datum> = Omit<
  LineGraphTypes.MasonryLineGraph<Datum>,
  'xAxisTickFormat' | 'xAxisTicks' | 'xAxisLabelTicks' | 'pointPadding'
> & {
  interval: MasonryChartInterval;
};

export function MasonryLineGraphIntervalSelector<Datum>({
  interval,
  ...props
}: MasonryLineGraphProps<Datum>) {
  const defaultTimezone = useGetDefaultTimezone();
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();

  const xAxisTicks = getIntervalXAxisTicks(
    props.data.map(props.accessors.xAccessor),
    interval,
    defaultTimezone,
  );
  const xAxisLabelTicks = getIntervalXAxisLabelTicks(
    props.data.map(props.accessors.xAccessor),
    interval,
    defaultTimezone,
  );

  if (interval === 'weekly') {
    return (
      <MasonryLineGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            weekday: 'short',
          })
        }
        pointPadding={0.5}
        {...props}
      />
    );
  }
  if (interval === 'monthly') {
    return (
      <MasonryLineGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        datumsPerColumn={1}
        xAxisTickFormat={formatMonthlyTick(
          formatDateWithUserProfileLocation,
          defaultTimezone,
        )}
        pointPadding={0.5}
        {...props}
      />
    );
  }
  if (interval === 'yearly') {
    return (
      <MasonryLineGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            month: 'narrow',
          })
        }
        pointPadding={0.5}
        {...props}
      />
    );
  }
  if (interval === '5-yearly') {
    return (
      <MasonryLineGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            year: 'numeric',
          })
        }
        pointPadding={0.5}
        {...props}
      />
    );
  }
  return null;
}
